/**
 * Registration form builder — the "add / remove the form fields" control on the
 * event editor.
 *
 * Every event always collects name / email / phone / organisation; this card
 * manages the *extra* questions on top of those. The rows live in the parent's
 * react-hook-form state under `form_fields_json`, so they autosave with the
 * rest of the editor and post as a plain array the API serialises to JSON.
 */
import { useRef } from "react";
import {
  Controller, useFieldArray, useWatch,
  type Control, type UseFormRegister, type UseFormSetValue,
} from "react-hook-form";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const FIELD_TYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Paragraph" },
  { value: "email", label: "Email" },
  { value: "tel", label: "Phone" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "select", label: "Dropdown" },
  { value: "checkbox", label: "Checkbox" },
] as const;

/** Mirrors `formFieldSchema` in krct-api/src/lib/eventFormFields.ts. */
export const formFieldSchema = z.object({
  key: z.string().min(1, "Required").max(60).regex(/^[a-z][a-z0-9_]*$/, "Lowercase letters, digits, underscores"),
  label: z.string().min(1, "Required").max(160),
  type: z.enum(["text", "textarea", "email", "tel", "number", "date", "select", "checkbox"]),
  required: z.boolean(),
  placeholder: z.string().max(160).nullable(),
  options: z.array(z.string().min(1).max(160)).max(50).nullable(),
});
export type EventFormField = z.infer<typeof formFieldSchema>;

/** Keys the built-in inputs already own — the API rejects these. */
const RESERVED = ["name", "email", "phone", "organization", "department"];

export const formFieldsSchema = z
  .array(formFieldSchema)
  .max(30, "At most 30 custom fields")
  .superRefine((fields, ctx) => {
    const seen = new Set<string>();
    fields.forEach((f, i) => {
      if (RESERVED.includes(f.key)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [i, "key"], message: `"${f.key}" is already collected` });
      }
      if (seen.has(f.key)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [i, "key"], message: "Duplicate key" });
      }
      seen.add(f.key);
      if (f.type === "select" && !(f.options ?? []).length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [i, "options"], message: "Add at least one option" });
      }
    });
  });

/** "Roll number" → "roll_number"; the stable id answers are stored under. */
export function toFieldKey(label: string): string {
  return label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s_]/g, "")
    .replace(/[\s_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^([0-9])/, "f$1")
    .slice(0, 60);
}

// The parent form is typed around the whole event row; threading a generic
// through for the one slice we touch buys nothing.
/* eslint-disable @typescript-eslint/no-explicit-any */
interface Props {
  control: Control<any>;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  errors?: any;
  disabled?: boolean;
}

export function EventFormFields({ control, register, setValue, errors, disabled }: Props) {
  const { fields, append, remove, move } = useFieldArray({ control, name: "form_fields_json" });
  // A key stops tracking its label as soon as the admin edits it by hand, so
  // rewording a question later never silently orphans answers already collected.
  const keyTouched = useRef<Set<string>>(new Set());

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Name, email, phone and organisation are always collected. Add any extra questions below — they appear on the
        public registration form and in the registrations table and CSV export.
      </p>

      {fields.length === 0 && (
        <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
          No extra questions yet.
        </p>
      )}

      {fields.map((f, i) => (
        <FieldRow
          key={f.id}
          rowId={f.id}
          index={i}
          isFirst={i === 0}
          isLast={i === fields.length - 1}
          control={control}
          register={register}
          setValue={setValue}
          errors={errors?.[i]}
          disabled={disabled}
          keyTouched={keyTouched}
          onMove={move}
          onRemove={() => { keyTouched.current.delete(f.id); remove(i); }}
        />
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled || fields.length >= 30}
        onClick={() =>
          append({ key: "", label: "", type: "text", required: false, placeholder: null, options: null } satisfies EventFormField)
        }
      >
        <Plus className="h-4 w-4" /> Add field
      </Button>
    </div>
  );
}

function FieldRow({
  rowId, index, isFirst, isLast, control, register, setValue, errors, disabled, keyTouched, onMove, onRemove,
}: {
  rowId: string;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  control: Control<any>;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  errors?: any;
  disabled?: boolean;
  keyTouched: React.MutableRefObject<Set<string>>;
  onMove: (from: number, to: number) => void;
  onRemove: () => void;
}) {
  // `fields` from useFieldArray is a snapshot taken when the array changed, so
  // it never sees the type the admin just picked. Watch the live value instead.
  const type = useWatch({ control, name: `form_fields_json.${index}.type` }) as EventFormField["type"] | undefined;

  return (
    <div className="space-y-3 rounded-md border bg-secondary/20 p-3">
      <div className="flex items-start gap-2">
        <div className="grid flex-1 gap-3 sm:grid-cols-[1fr_170px]">
          <div className="space-y-1.5">
            <Label htmlFor={`ff-label-${rowId}`} className="text-xs">Question</Label>
            <Input
              id={`ff-label-${rowId}`}
              placeholder="Roll number"
              disabled={disabled}
              {...register(`form_fields_json.${index}.label`, {
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  if (keyTouched.current.has(rowId)) return;
                  const next = toFieldKey(e.target.value);
                  setValue(`form_fields_json.${index}.key`, next, { shouldDirty: true, shouldValidate: true });
                },
              })}
            />
            {errors?.label && <p className="text-xs text-destructive">{errors.label.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`ff-type-${rowId}`} className="text-xs">Answer type</Label>
            <Controller
              control={control}
              name={`form_fields_json.${index}.type`}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
                  <SelectTrigger id={`ff-type-${rowId}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1 pt-6">
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={disabled || isFirst}
            aria-label="Move up" onClick={() => onMove(index, index - 1)}>
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={disabled || isLast}
            aria-label="Move down" onClick={() => onMove(index, index + 1)}>
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" disabled={disabled}
            aria-label="Remove field" onClick={onRemove}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-[200px_1fr_auto]">
        <div className="space-y-1.5">
          <Label htmlFor={`ff-key-${rowId}`} className="text-xs">Key</Label>
          <Input
            id={`ff-key-${rowId}`}
            className="font-mono text-xs"
            disabled={disabled}
            {...register(`form_fields_json.${index}.key`, {
              onChange: () => keyTouched.current.add(rowId),
            })}
          />
          {errors?.key && <p className="text-xs text-destructive">{errors.key.message}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`ff-extra-${rowId}`} className="text-xs">
            {type === "select" ? "Options (comma-separated)" : "Placeholder / hint"}
          </Label>
          {type === "select" ? (
            <Controller
              control={control}
              name={`form_fields_json.${index}.options`}
              render={({ field }) => (
                <Input
                  id={`ff-extra-${rowId}`}
                  placeholder="Veg, Non-veg, Jain"
                  disabled={disabled}
                  value={(field.value ?? []).join(", ")}
                  onChange={(e) => field.onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                />
              )}
            />
          ) : (
            <Input id={`ff-extra-${rowId}`} placeholder="Optional" disabled={disabled}
              {...register(`form_fields_json.${index}.placeholder`)} />
          )}
          {errors?.options && <p className="text-xs text-destructive">{errors.options.message}</p>}
        </div>
        <div className="flex items-end gap-2 pb-2">
          <Controller
            control={control}
            name={`form_fields_json.${index}.required`}
            render={({ field }) => (
              <Switch id={`ff-req-${rowId}`} checked={!!field.value} onCheckedChange={field.onChange} disabled={disabled} />
            )}
          />
          <Label htmlFor={`ff-req-${rowId}`} className="cursor-pointer text-xs">Required</Label>
        </div>
      </div>
    </div>
  );
}
/* eslint-enable @typescript-eslint/no-explicit-any */
